import * as React from 'react';
import classNames from 'classnames';
import { noop } from '@deltasierra/utilities-object';
import { useChange } from '@deltasierra/react-hooks';

export type TextInputProps = Omit<PuppetTextInputProps, 'onChange' | 'value'> & {
    initialValue?: string;
    onChange?: (value: string) => void;
};

export const TextInput: React.FC<TextInputProps> = ({ initialValue, onChange, ...props }) => {
    const [value, setValue] = useChange(initialValue || '', onChange || noop);
    return (
        <PuppetTextInput
            {...props}
            value={value}
            onChange={event => setValue(event.target.value)}
            onClear={() => setValue('')}
        />
    );
};

export type PuppetTextInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
    clearable?: boolean;
    'data-cy'?: string;
    onClear?: () => void;
    ref?: React.Ref<HTMLInputElement>;
    value: string;
    fullWidth?: boolean;
};

export const PuppetTextInput: React.FC<PuppetTextInputProps> = React.forwardRef(
    ({ clearable, fullWidth, onClear, ...props }: PuppetTextInputProps, ref: React.Ref<HTMLInputElement>) => {
        const classNameObj = classNames(props.className, 'form-control');
        const showClear = clearable && props.value.length > 0;

        const renderInput = <input {...props} className={classNameObj} ref={ref} />;

        if (!clearable) {
            return renderInput;
        }

        return (
            <div className="btn-group" style={{ width: fullWidth ? '100%' : 'auto' }}>
                {renderInput}
                <InputClearButton hidden={!showClear} onClick={onClear} />
            </div>
        );
    },
) as any;

PuppetTextInput.displayName = 'PuppetTextInput';

export type InputClearButtonProps = {
    className?: string;
    onClick?: () => void;
    hidden?: boolean;
};

export const InputClearButton: React.FC<InputClearButtonProps> = props => {
    const className = classNames(
        props.className,
        'form-control-clear',
        'glyphicon',
        'glyphicon-remove',
        'form-control-feedback',
        { hidden: props.hidden },
    );

    return <span className={className} {...props} style={{ cursor: 'pointer', pointerEvents: 'auto', top: 0 }}></span>;
};
